/**
 * `createApiTracersSource` — the `pipeline-mgmt-api` tracers port over the shared
 * `ApiClient`.
 *
 * Tracers are "follow the money" flow-tracking definitions; each run snapshots
 * the discovered `InteractionGraph` (which `@authe/tx-graph` renders). The
 * backend speaks snake_case (`max_hops`, `min_value_raw`, seeds as
 * `{chain_id, value}`), so this adapter maps both directions and exposes a
 * camelCase domain to the feature page. Canonical `/tracers/*` paths are mapped
 * to `private/pipeline-mgmt-api` by `RESOURCE_REWRITES`.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";
import {
  asNullableString,
  asNumber,
  asOptString,
  asRecord,
  asString,
  asStringList,
  rowsAt,
} from "./wire";
import { walkPages } from "./pagedList";

export type FlowDirection = "outbound" | "inbound" | "both";
export type TracerStatus = "active" | "paused" | "archived";
export type TracerScope = "user" | "team" | "tenant" | "org" | "customer" | "system";

export interface TracerSeed {
  chainId: string;
  value: string;
}

export interface TracerSummary {
  id: string;
  name: string;
  description?: string | null;
  scope: TracerScope;
  status: TracerStatus;
  direction: FlowDirection;
  maxHops: number;
  seeds: TracerSeed[];
  chainIds: string[];
  tokenFilters: string[];
  minValueRaw?: string | null;
  followBridges: boolean;
  autoRun: boolean;
  lastRunId?: string | null;
  lastRunAt?: number | null;
  runCount: number;
  /** Teams this tracer is shared with (backend ABAC `visible_to_team_ids`). */
  visibleToTeamIds: string[];
}

/** A node/edge graph snapshot shaped like the backend `InteractionGraph`. */
export interface TracerRunGraph {
  nodes: Record<string, unknown>[];
  edges: Record<string, unknown>[];
  node_count: number;
  edge_count: number;
  chain_ids: string[];
  multi_chain: boolean;
}

/**
 * How much of an answer a run's graph is.
 *
 * `ok` is the ONLY one from which "no edges" may be read as a statement about
 * the chain — that value did not move. `unavailable` means no flow data source
 * was wired, so nothing was traced; the page must not render it as an empty
 * result. Kept in step with `commons.interfaces.models.tracer_run`.
 */
export const TRACER_RUN_STATUSES = [
  "running",
  "ok",
  "partial",
  "unavailable",
  "failed",
] as const;
export type TracerRunStatus = (typeof TRACER_RUN_STATUSES)[number];

export interface TracerRunSummary {
  id: string;
  status: TracerRunStatus;
  /** Why the graph is not a complete answer. `null` when it is one. */
  statusReason: string | null;
  hopsWalked: number;
  nodeCount: number;
  edgeCount: number;
  totalValueRaw: string;
  terminalNodeCount: number;
  startedAt?: number | null;
  finishedAt?: number | null;
  durationMs?: number | null;
  error?: string | null;
  createdAt?: number | null;
  graph: TracerRunGraph;
}

export interface CreateTracerInput {
  name: string;
  description?: string | null;
  direction: FlowDirection;
  maxHops: number;
  seeds: TracerSeed[];
  chainIds?: string[];
  tokenFilters?: string[];
  minValueRaw?: string | null;
  followBridges?: boolean;
  autoRun?: boolean;
  scope?: TracerScope;
  /** Teams to share the tracer with (backend ABAC `visible_to_team_ids`). */
  visibleToTeamIds?: string[];
}

export interface TracersSource {
  list(): Promise<TracerSummary[]>;
  get(id: string): Promise<TracerSummary>;
  listRuns(id: string): Promise<TracerRunSummary[]>;
  create(input: CreateTracerInput): Promise<TracerSummary>;
  update(id: string, patch: Partial<CreateTracerInput> & { status?: TracerStatus }): Promise<TracerSummary>;
  remove(id: string): Promise<void>;
  runNow(id: string): Promise<{ queued: boolean; tracerId: string }>;
}

function seedFromWire(raw: Record<string, unknown>): TracerSeed {
  return { chainId: asString(raw.chain_id ?? raw.chainId), value: asString(raw.value) };
}

function tracerFromWire(raw: Record<string, unknown>): TracerSummary {
  return {
    id: asString(raw.id),
    name: asString(raw.name),
    description: asNullableString(raw.description),
    scope: asString(raw.scope, "user") as TracerScope,
    status: asString(raw.status, "active") as TracerStatus,
    direction: asString(raw.direction, "outbound") as FlowDirection,
    maxHops: Number(raw.max_hops ?? raw.maxHops ?? 3),
    seeds: rowsAt(raw, "seeds").map(seedFromWire),
    chainIds: asStringList(raw.chain_ids ?? raw.chainIds),
    tokenFilters: asStringList(raw.token_filters ?? raw.tokenFilters),
    minValueRaw: asNullableString(raw.min_value_raw ?? raw.minValueRaw),
    followBridges: Boolean(raw.follow_bridges ?? raw.followBridges ?? true),
    autoRun: Boolean(raw.auto_run ?? raw.autoRun ?? true),
    lastRunId: asNullableString(raw.last_run_id ?? raw.lastRunId),
    lastRunAt: asNumber(raw.last_run_at ?? raw.lastRunAt),
    runCount: Number(raw.run_count ?? raw.runCount ?? 0),
    visibleToTeamIds: asStringList(raw.visible_to_team_ids ?? raw.visibleToTeamIds),
  };
}

function tracerToWire(input: Partial<CreateTracerInput> & { status?: TracerStatus }): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (input.name !== undefined) body.name = input.name;
  if (input.description !== undefined) body.description = input.description;
  if (input.direction !== undefined) body.direction = input.direction;
  if (input.maxHops !== undefined) body.max_hops = input.maxHops;
  if (input.seeds !== undefined) {
    body.seeds = input.seeds.map((s) => ({ chain_id: s.chainId, value: s.value }));
  }
  if (input.chainIds !== undefined) body.chain_ids = input.chainIds;
  if (input.tokenFilters !== undefined) body.token_filters = input.tokenFilters;
  if (input.minValueRaw !== undefined) body.min_value_raw = input.minValueRaw;
  if (input.followBridges !== undefined) body.follow_bridges = input.followBridges;
  if (input.autoRun !== undefined) body.auto_run = input.autoRun;
  if (input.scope !== undefined) body.scope = input.scope;
  if (input.status !== undefined) body.status = input.status;
  if (input.visibleToTeamIds !== undefined) body.visible_to_team_ids = input.visibleToTeamIds;
  return body;
}

function runFromWire(raw: Record<string, unknown>): TracerRunSummary {
  const nodes = rowsAt(raw, "nodes");
  const edges = rowsAt(raw, "edges");
  return {
    id: asString(raw.id),
    // NOT `?? "ok"`. A status this build does not recognise used to land on
    // the one value that licenses reading an empty graph as "the money did not
    // move" — so a backend that grows a status makes this page state, with
    // confidence, the most consequential thing it can say. `running` is the
    // non-committal fallback: it renders no claim about the chain at all.
    status: (TRACER_RUN_STATUSES as readonly string[]).includes(asString(raw.status))
      ? (asString(raw.status) as TracerRunStatus)
      : "running",
    statusReason: asNullableString(raw.status_reason),
    hopsWalked: Number(raw.hops_walked ?? 0),
    nodeCount: Number(raw.node_count ?? nodes.length),
    edgeCount: Number(raw.edge_count ?? edges.length),
    totalValueRaw: asString(raw.total_value_raw, "0"),
    terminalNodeCount: Number(raw.terminal_node_count ?? 0),
    startedAt: asNumber(raw.started_at),
    finishedAt: asNumber(raw.finished_at),
    durationMs: asNumber(raw.duration_ms),
    error: asNullableString(raw.error),
    createdAt: asNumber(raw.created_at),
    graph: {
      nodes,
      edges,
      node_count: Number(raw.node_count ?? nodes.length),
      edge_count: Number(raw.edge_count ?? edges.length),
      chain_ids: asStringList(raw.chain_ids),
      multi_chain: Boolean(raw.multi_chain ?? false),
    },
  };
}

export function createApiTracersSource(client: ApiClient): TracersSource {
  const tracerPath = (id: string) => `/tracers/${encodeURIComponent(id)}`;

  return {
    async list(): Promise<TracerSummary[]> {
      // Paged. `/tracers` layers the system defaults in front of the caller's
      // own and filters for visibility after taking the page, so this saw the
      // first fifty rows minus whatever it could not see — on a management
      // surface, where a missing tracer reads as one that does not exist.
      return (await walkPages(client, "/tracers")).map(tracerFromWire);
    },
    async get(id: string): Promise<TracerSummary> {
      return tracerFromWire(asRecord(unwrapData(await client.get<unknown>(tracerPath(id)))));
    },
    async listRuns(id: string): Promise<TracerRunSummary[]> {
      // Paged too, and here the truncation was VISIBLE and contradictory: the
      // sidebar renders the tracer's server-maintained `runCount` right beside
      // this list, so a tracer with eighty runs said "80 runs" above a list of
      // fifty that simply stopped.
      return (await walkPages(client, `${tracerPath(id)}/runs`)).map(runFromWire);
    },
    async create(input: CreateTracerInput): Promise<TracerSummary> {
      return tracerFromWire(
        asRecord(unwrapData(await client.post<unknown>("/tracers", tracerToWire(input)))),
      );
    },
    async update(id, patch): Promise<TracerSummary> {
      return tracerFromWire(
        asRecord(unwrapData(await client.patch<unknown>(tracerPath(id), tracerToWire(patch)))),
      );
    },
    async remove(id: string): Promise<void> {
      unwrapData(await client.delete<void>(tracerPath(id)));
    },
    async runNow(id: string): Promise<{ queued: boolean; tracerId: string }> {
      const raw = asRecord(unwrapData(await client.post<unknown>(`${tracerPath(id)}/run`, {})));
      return { queued: Boolean(raw.queued), tracerId: asOptString(raw.tracer_id) ?? id };
    },
  };
}
